import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertIcon, CheckIcon, PlatformIcon, TrashIcon } from './Icons';
import { useNotificationsStore } from '../store/notificationsStore';
import { formatDateTime } from '../utils/format';
import { isSocialNotification, normalizePlatform } from '../features/notifications/notificationMeta';
import { useI18n } from '../i18n/I18nProvider';

/** Polling interval for the unread count (ms). */
const POLL_INTERVAL_MS = 45_000;

/**
 * NotificationBell — navbar dropdown.
 *
 * Shows an unread count badge that refreshes on an interval, and when
 * opened, loads the full recent list. Clicking a notification marks it
 * read and navigates to its `link` (if provided). "Mark all read" empties
 * the count.
 */
function BellSvg({ hasUnread }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={hasUnread ? 'text-blue-600 dark:text-sky-400' : 'text-slate-500 dark:text-slate-400'}
    >
      <path d="M18 8a6 6 0 0 0-9.33-5M6 8a6 6 0 0 0 0 8h12a6 6 0 0 0 0-8" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function typeIcon(type) {
  if (isSocialNotification(type)) {
    return type === 'social_message' ? (
      <AlertIcon width={14} height={14} className="text-blue-500" />
    ) : (
      <AlertIcon width={14} height={14} className="text-blue-500" />
    );
  }

  switch (type) {
    case 'publish_success':
      return <CheckIcon width={14} height={14} className="text-emerald-500" />;
    case 'publish_failure':
      return <AlertIcon width={14} height={14} className="text-red-500" />;
    case 'token_expired':
      return <AlertIcon width={14} height={14} className="text-amber-500" />;
    default:
      return <AlertIcon width={14} height={14} className="text-gray-400" />;
  }
}

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'x', label: 'X' },
];

export default function NotificationBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const notifications = useNotificationsStore((s) => s.notifications);
  const loading = useNotificationsStore((s) => s.loading);
  const loaded = useNotificationsStore((s) => s.loaded);
  const fetch = useNotificationsStore((s) => s.fetch);
  const refreshCount = useNotificationsStore((s) => s.refreshCount);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const remove = useNotificationsStore((s) => s.remove);
  const filterPlatform = useNotificationsStore((s) => s.filters.platform);
  const setPlatformFilter = useNotificationsStore((s) => s.setPlatformFilter);

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Initial count + periodic poll. Only runs while the component is mounted
  // (i.e. while the navbar is visible — any authed page).
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Lazy-load the full list on first open.
  useEffect(() => {
    if (open && !loaded) fetch();
  }, [open, loaded, fetch]);

  // Outside click + Escape to close.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`${t('common.notifications')}${unreadCount ? ` (${unreadCount})` : ''}`}
        aria-expanded={open}
        className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/60 bg-white/90 text-slate-600 shadow-sm backdrop-blur-sm transition hover:border-blue-200 hover:bg-gradient-to-br hover:from-blue-50/95 hover:to-sky-50/60 hover:text-blue-900 active:scale-[0.97] dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-sky-500/40 dark:hover:from-slate-800 dark:hover:to-blue-950/35 dark:hover:text-sky-100 [&_svg]:h-[20px] [&_svg]:w-[20px]"
      >
        <BellSvg hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <span className="absolute -end-0.5 -top-0.5 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:ring-gray-950">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="dropdown-panel absolute end-0 z-30 mt-2 w-96 max-w-[calc(100vw-1rem)]"
          >
            <div className="dropdown-panel-header pb-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                  {t('common.notifications')}
                </p>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="rounded-lg px-2 py-1 text-xs font-semibold text-blue-600 transition hover:bg-white/80 hover:text-blue-700 dark:text-sky-400 dark:hover:bg-slate-800/80 dark:hover:text-sky-200"
                  >
                    {t('common.markAllRead')}
                  </button>
                )}
              </div>
              <div className="mt-2.5">
                <select
                  value={filterPlatform}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="select-native select-native-sm input-base w-full border-slate-200/95 dark:border-slate-600"
                >
                  {PLATFORM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value === 'all' ? t('common.all') : option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto py-1">
              {loading && !loaded ? (
                <div className="px-4 py-6 text-center text-xs text-slate-500 dark:text-slate-400">
                  {t('common.loading')}
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">You're all caught up</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Publish success, failure, and account alerts will show here.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800/90">
                  {notifications.map((n) => (
                    <li key={n.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleClick(n)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick(n);
                          }
                        }}
                        className={`dropdown-item mx-1 my-0.5 !cursor-pointer !items-start gap-3 px-3 py-2.5 ${
                          !n.is_read ? 'bg-blue-50/70 dark:bg-blue-950/30' : ''
                        }`}
                      >
                        <span className="mt-1 flex h-5 w-5 items-center justify-center">
                          {typeIcon(n.type)}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="text-sm font-medium line-clamp-1">{n.title}</span>
                            {!n.is_read && (
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500 dark:bg-sky-400" />
                            )}
                          </span>
                          {n.message && (
                            <span className="block mt-0.5 text-xs text-slate-500 line-clamp-2">
                              {n.message}
                            </span>
                          )}
                          <span className="block mt-1 text-[11px] text-slate-400">
                            {formatDateTime(n.created_at)}
                          </span>
                          {normalizePlatform(n?.data?.platform) && (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-slate-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-300">
                              <PlatformIcon
                                platform={normalizePlatform(n.data.platform)}
                                width={12}
                                height={12}
                              />
                              {normalizePlatform(n.data.platform)}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              remove(n.id);
                            }}
                            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                            aria-label="Delete notification"
                          >
                            <TrashIcon width={14} height={14} />
                          </button>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
